import { ArrowDropDown } from '@mui/icons-material';
import { alpha, Box, Button, ButtonProps, Menu, useTheme } from '@mui/material';
import React, { useCallback, useState } from 'react';

interface SplitMenuButtonProps extends Omit<ButtonProps, 'endIcon'> {
	menuChildren: React.ReactNode;
	children: React.ReactNode;
}

const SplitMenuButton: React.FC<SplitMenuButtonProps> = ({
	menuChildren,
	children,
	onClick,
	...buttonProps
}) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const {
		spacing,
		palette: {
			mode,
			grey: { '200': grey200 }
		}
	} = useTheme();

	const handleCaretClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			setAnchorEl(event.currentTarget);
		},
		[]
	);

	/* istanbul ignore next */
	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const handleMenuClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			setAnchorEl(null);
		},
		[]
	);

	const handleButtonClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onClick?.(event);
		},
		[onClick]
	);

	return (
		<Box sx={{ display: 'flex' }}>
			<Button
				{...buttonProps}
				onClick={handleButtonClick}
				sx={{
					borderTopRightRadius: 0,
					borderBottomRightRadius: 0,
					borderRight: 'none',
					...buttonProps.sx
				}}
			>
				{children}
			</Button>
			<Button
				variant={buttonProps.variant}
				color={buttonProps.color}
				disabled={buttonProps.disabled}
				size={buttonProps.size}
				onClick={handleCaretClick}
				sx={{
					borderTopLeftRadius: 0,
					borderBottomLeftRadius: 0,
					minWidth: 'auto',
					px: 0.5
				}}
				aria-label="dropdown menu"
				aria-haspopup="true"
			>
				<ArrowDropDown />
			</Button>
			<Menu
				onClick={handleMenuClick}
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right'
				}}
				slotProps={{
					paper: {
						sx: {
							boxShadow:
								mode === 'light'
									? `0 0 ${spacing(1.5)} 0 ${alpha(grey200, 1)}`
									: null
						}
					}
				}}
			>
				{menuChildren}
			</Menu>
		</Box>
	);
};

export default SplitMenuButton;
